import React, { useEffect, useRef, useState } from 'react';
import { getProfile, updateProfile, uploadAvatar, changePassword, UserProfile } from './api';
import NotificationsCard from './NotificationsCard';

export default function Profile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [form, setForm] = useState({ tiktok_username: '', first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok?: string; err?: string } | null>(null);

  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getProfile().then(r => {
      setProfile(r.data);
      setForm({ tiktok_username: r.data.tiktok_username ?? '', first_name: r.data.first_name ?? '', last_name: r.data.last_name ?? '', email: r.data.email ?? '', phone: r.data.phone ?? '' });
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setSavedMsg('');
    try {
      const updated = await updateProfile({ tiktok_username: form.tiktok_username || null, first_name: form.first_name || null, last_name: form.last_name || null, email: form.email || null, phone: form.phone || null });
      setProfile(updated.data); setSavedMsg('Profil sauvegardé');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch { setSavedMsg('Erreur lors de la sauvegarde'); }
    setSaving(false);
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAvatarUploading(true);
    try { const r = await uploadAvatar(file); setProfile(p => p ? { ...p, avatar_url: r.data.avatar_url } : p); } catch {}
    setAvatarUploading(false); e.target.value = '';
  };

  const handlePasswordChange = async () => {
    if (!pwForm.current || !pwForm.next) { setPwMsg({ err: 'Remplis tous les champs' }); return; }
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ err: 'Les mots de passe ne correspondent pas' }); return; }
    if (pwForm.next.length < 6) { setPwMsg({ err: 'Minimum 6 caractères' }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      await changePassword(pwForm.current, pwForm.next);
      setPwMsg({ ok: 'Mot de passe changé avec succès' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err: any) { setPwMsg({ err: err?.response?.data?.detail || 'Mot de passe actuel incorrect' }); }
    setPwSaving(false);
  };

  if (!profile) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Chargement…</div>
      </div>
    );
  }

  const avatarSrc = profile.avatar_url ?? null;
  const initials = ((profile.first_name?.[0] ?? '') + (profile.last_name?.[0] ?? '')).toUpperCase() || profile.username.slice(0, 2).toUpperCase();

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '24px 32px' }}>
        <h1 style={{ margin: 0, color: '#0f172a', fontSize: 22, fontWeight: 700 }}>Mon profil</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Gérez vos informations personnelles et votre mot de passe</p>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
        <div style={{ maxWidth: 580, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Photo */}
          <Card title="Photo de profil">
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', background: profile.role === 'admin' ? '#ede9fe' : '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${profile.role === 'admin' ? '#ddd6fe' : '#bbf7d0'}` }}>
                  {avatarSrc ? (
                    <img src={avatarSrc} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => (e.target as HTMLImageElement).style.display = 'none'} />
                  ) : (
                    <span style={{ color: profile.role === 'admin' ? '#7c3aed' : '#16a34a', fontSize: 24, fontWeight: 700 }}>{initials}</span>
                  )}
                </div>
                {avatarUploading && (
                  <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#7c3aed', fontSize: 10 }}>…</span>
                  </div>
                )}
              </div>
              <div>
                <button onClick={() => avatarRef.current?.click()} style={btnSecondary}>Changer la photo</button>
                <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 4 }}>PNG, JPG ou WebP — max 5 MB</div>
                <input ref={avatarRef} type="file" accept=".png,.jpg,.jpeg,.webp" style={{ display: 'none' }} onChange={handleAvatarChange} />
              </div>
            </div>
          </Card>

          {/* Infos */}
          <Card title="Informations personnelles">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <LightField label="Prénom">
                <input style={inputLight} value={form.first_name} placeholder="Jean" onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
              </LightField>
              <LightField label="Nom">
                <input style={inputLight} value={form.last_name} placeholder="Dupont" onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
              </LightField>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <LightField label="Adresse e-mail">
                <input style={inputLight} type="email" value={form.email} placeholder="jean@example.com" onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </LightField>
              <LightField label="📱 Téléphone mobile">
                <input style={inputLight} type="tel" value={form.phone} placeholder="+33 6 12 34 56 78" onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Pour recevoir tes <strong>rappels de match par SMS</strong> avant chaque live.
                </div>
              </LightField>
              <LightField label="Identifiant TikTok">
                <div style={{ display: 'flex' }}>
                  <span style={{ padding: '9px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRight: 'none', borderRadius: '8px 0 0 8px', color: '#94a3b8', fontSize: 13 }}>@</span>
                  <input style={{ ...inputLight, borderRadius: '0 8px 8px 0', flex: 1 }} value={form.tiktok_username} placeholder="monpseudo" onChange={e => setForm(f => ({ ...f, tiktok_username: e.target.value }))} />
                </div>
              </LightField>
              <LightField label="Nom d'utilisateur (connexion)">
                <input style={{ ...inputLight, background: '#f8fafc', color: '#94a3b8' }} value={profile.username} disabled />
              </LightField>
            </div>
            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
              {savedMsg && <span style={{ color: savedMsg.includes('Erreur') ? '#dc2626' : '#16a34a', fontSize: 13 }}>{savedMsg}</span>}
            </div>
          </Card>

          {/* Notifications */}
          <NotificationsCard />

          {/* Mot de passe */}
          <Card title="Changer le mot de passe">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <LightField label="Mot de passe actuel">
                <input style={inputLight} type="password" value={pwForm.current} placeholder="••••••••" onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} />
              </LightField>
              <LightField label="Nouveau mot de passe">
                <input style={inputLight} type="password" value={pwForm.next} placeholder="••••••••" onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} />
              </LightField>
              <LightField label="Confirmer le nouveau mot de passe">
                <input style={inputLight} type="password" value={pwForm.confirm} placeholder="••••••••" onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
              </LightField>
            </div>
            {pwMsg && (
              <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, background: pwMsg.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${pwMsg.ok ? '#bbf7d0' : '#fecaca'}`, color: pwMsg.ok ? '#16a34a' : '#dc2626', fontSize: 13 }}>
                {pwMsg.ok || pwMsg.err}
              </div>
            )}
            <button onClick={handlePasswordChange} disabled={pwSaving} style={{ ...btnSecondary, marginTop: 14 }}>
              {pwSaving ? 'Changement…' : 'Changer le mot de passe'}
            </button>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 20px' }}>
      <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function LightField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ color: '#374151', fontSize: 13, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const inputLight: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', fontSize: 13, boxSizing: 'border-box', outline: 'none' };
const btnPrimary: React.CSSProperties = { padding: '9px 22px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer' };
